'use client';

import { submitContactForm, type ContactFormData, type ContactResponse } from '@/actions/contactAction';
import { FormEvent, useCallback, useRef, useState } from 'react';


import ReCaptcha, { type ReCaptchaHandle } from './ReCaptcha';

// Get reCAPTCHA site key from environment variable
const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY || '';

const FormContact = () => {
	const [formData, setFormData] = useState<ContactFormData>({
		email: '',
		subject: '',
		message: '',
		recaptchaToken: undefined
	});

	// Update reCAPTCHA token when verified
	const handleVerifyRecaptcha = useCallback((token: string) => {
		setFormData(prev => ({
			...prev,
			recaptchaToken: token
		}));
	}, []);
	const [validated, setValidated] = useState(false);
	const [submitStatus, setSubmitStatus] = useState<ContactResponse | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const recaptchaRef = useRef<ReCaptchaHandle>(null);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { id, value } = e.target;
		setFormData(prev => ({
			...prev,
			[id === 'contactEmail' ? 'email' :
				id === 'contactSubject' ? 'subject' : 'message']: value
		}));
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const form = e.currentTarget;

		if (!form.checkValidity()) {
			e.stopPropagation();
			setValidated(true);
			return;
		}

		// Make sure we have a reCAPTCHA token
		if (!formData.recaptchaToken) {
			setSubmitStatus({
				success: false,
				message: 'reCAPTCHA verificatie wordt geladen. Probeer het opnieuw in een paar seconden.'
			});
			return;
		}

		setIsSubmitting(true);
		setValidated(true);

		try {
			// Call the server action with the form data (including reCAPTCHA token)
			const response = await submitContactForm(formData);
			setSubmitStatus(response);

			// If successful, reset the form
			if (response.success) {
				setFormData({
					email: '',
					subject: '',
					message: '',
					recaptchaToken: undefined
				});
				setValidated(false);
			}
		} catch (error) {
			setSubmitStatus({
				success: false,
				message: error instanceof Error ? error.message : 'Er is iets misgegaan bij het verzenden.'
			});
		} finally {
			setIsSubmitting(false);

			// Refresh reCAPTCHA token after submission (resolves timeout-or-duplicate error)
			recaptchaRef.current?.refresh();
		}
	};

	return (
		<div className="card-form">
			<h1>Contact</h1>

			{submitStatus && (
				<div>
					<div>
						<div className={submitStatus.success ? 'success' : 'danger'}>
							{submitStatus.message}
						</div>
					</div>
				</div>
			)}

			{(!submitStatus || !submitStatus.success) && (
				<form noValidate onSubmit={handleSubmit}>

					{/* Load reCAPTCHA invisibly */}
					<ReCaptcha
						ref={recaptchaRef}
						siteKey={RECAPTCHA_SITE_KEY}
						action="contact_form"
						onVerify={handleVerifyRecaptcha}
					/>


					<div>
						<label>E-mailadres *</label>
						<input
							type="email"
							value={formData.email}
							onChange={handleChange}
							required
						/>
						<span className="invalid-feedback">
							Vul een geldig e-mailadres in.
						</span>
					</div>

					<div>
						<label>Onderwerp *</label>
						<input
							type="text"
							value={formData.subject}
							onChange={handleChange}
							required
						/>
						<span className="invalid-feedback">
							Vul een onderwerp in.
						</span>
					</div>

					<div>
						<label>Uw bericht *</label>
						<textarea
							rows={8}
							value={formData.message}
							onChange={handleChange}
							required
						/>
						<span className="invalid-feedback">
							Vul een bericht in.
						</span>
					</div>

					<div>
						<button
							type="submit"
							disabled={isSubmitting}
						>
							{isSubmitting ? 'Bezig met verzenden...' : 'Versturen'}
						</button>
					</div>

				</form>
			)}
		</div>
	);
};

export default FormContact;
